import { useEffect, useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { useDebounce } from '@src/hooks/useDebounce';
import { useVirtualBackgroundStore } from '@src/stores';
import { virtualBackgroundService } from '@src/services';

// Configuration update constants
const CONFIG_UPDATE_DEBOUNCE_MS = 1000; // Debounce config updates to prevent excessive worker updates

/**
 * Config Watcher Hook
 * Synchronizes store config changes to worker in real-time
 *
 * Only syncs when an effect is active to avoid loading worker unnecessarily
 */
const useVirtualBackgroundConfig = () => {
  const { config, appliedEffect, selectedEffect } = useVirtualBackgroundStore(
    useShallow((state) => ({
      config: state.config,
      appliedEffect: state.appliedEffect,
      selectedEffect: state.selectedEffect,
    }))
  );

  const debouncedConfig = useDebounce(config, CONFIG_UPDATE_DEBOUNCE_MS);

  const hasActiveEffect = useMemo(
    () => appliedEffect.type !== 'none' || selectedEffect.type !== 'none',
    [appliedEffect, selectedEffect]
  );

  useEffect(() => {
    // Skip if no effect is active - don't load worker unnecessarily
    if (!hasActiveEffect) {
      return;
    }

    const updateWorker = async () => {
      try {
        // OPTIMIZATION: Only update config if worker is ALREADY active.
        // We use getActiveWorker (peek) to avoid incrementing Ref Count or creating a new worker.
        // This avoids the need for acquire/release logic here.
        const workerApi = await virtualBackgroundService.getActiveWorker();

        if (workerApi && workerApi.setConfig) {
          await workerApi.setConfig(debouncedConfig);
        }
      } catch (error) {
        console.error('[VB] Failed to update worker config:', error);
      }
    };

    updateWorker();
  }, [debouncedConfig, hasActiveEffect]);
};

export default useVirtualBackgroundConfig;
